import React, { useState } from "react";
import PropTypes from "prop-types";

/**
 * Enhanced Info Tooltip Component with diagonal directions
 * @param {React.ReactNode} children - Trigger element
 * @param {string} text - Tooltip text/content
 * @param {'top' | 'right' | 'bottom' | 'left' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'} direction - Tooltip direction
 */
const InfoTooltip = ({ children, text, direction = "top" }) => {
  const [isVisible, setIsVisible] = useState(false);

  // ✅ Position classes for tooltip window
  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-3",
    right: "left-full top-1/2 -translate-y-1/2 ml-3",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-3",
    left: "right-full top-1/2 -translate-y-1/2 mr-3",
    "top-right": "bottom-full right-0 mb-3",
    "top-left": "bottom-full left-0 mb-3",
    "bottom-right": "top-full right-0 mt-3",
    "bottom-left": "top-full left-0 mt-3",
  };

  // ✅ Arrow position classes
  const arrowClasses = {
    top: "top-full left-1/2 -translate-x-1/2",
    "top-right": "top-full right-4",
    "top-left": "top-full left-4",
    right: "right-full top-1/2 -translate-y-1/2",
    bottom: "bottom-full left-1/2 -translate-x-1/2",
    "bottom-right": "bottom-full right-4",
    "bottom-left": "bottom-full left-4",
    left: "left-full top-1/2 -translate-y-1/2",
  };

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}

      {isVisible && (
        <div
          className={`absolute z-50 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg whitespace-nowrap ${positionClasses[direction] || positionClasses.top}`}
        >
          {/* Tooltip text */}
          {text}

          {/* Arrow */}
          <div
            className={`absolute w-2.5 h-2.5 bg-gray-800 rotate-120 ${arrowClasses[direction] || arrowClasses.top}`}
            style={{
              // Slight offset to tuck the arrow seamlessly into the tooltip edge
              clipPath: "polygon(0 0, 100% 0, 100% 100%)",
            }}
          />
        </div>
      )}
    </div>
  );
};


InfoTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  text: PropTypes.string.isRequired,
  direction: PropTypes.oneOf([
    "top",
    "right",
    "bottom",
    "left",
    "top-right",
    "top-left",
    "bottom-right",
    "bottom-left",
  ]),
};

export default InfoTooltip;
